import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  assertUnreachable,
  formatCents,
} from '@social-garden/utils/helpers.ts';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useCallback } from 'react';
import { myCampaignReportsDocument } from '@social-garden/api/documents/campaign.ts';
import { getChannelTypeName } from '@social-garden/utils/channel.ts';
import {
  getContentTypeFromCampaignType,
  getContentTypeNameFromCampaignType,
} from '@social-garden/utils/content.ts';
import PageTitle from '@/components/PageTitle.tsx';
import BrandSelect from '@/components/BrandSelect.tsx';
import useInfiniteScrollPagination from '@/hooks/useInfiniteScrollPagination.ts';
import useBrandIdsQueryState from '@/hooks/useBrandIdsQueryState.ts';
import ChannelTypeChip from '@/components/ChannelTypeChip.tsx';
import ChannelContentTypeChip from '@/components/ChannelContentTypeChip.tsx';
import Link from '@/components/Link.tsx';
import { CURRENCY } from '@/constants/Currency.ts';

export const Route = createFileRoute('/_manager/_app/campaigns/reports')({
  component: CampaignReports,
});

function getFollowerOrSubscriberCount(
  reportable:
    | { __typename: 'InstagramReelCampaignReport'; followerCount: number }
    | { __typename: 'InstagramPostCampaignReport'; followerCount: number }
    | { __typename: 'InstagramStoryCampaignReport'; followerCount: number }
    | { __typename: 'TiktokVideoCampaignReport'; followerCount: number }
    | { __typename: 'YoutubeVideoCampaignReport'; subscriberCount: number },
) {
  switch (reportable.__typename) {
    case 'InstagramReelCampaignReport':
    case 'InstagramPostCampaignReport':
    case 'InstagramStoryCampaignReport': {
      return reportable.followerCount;
    }
    case 'TiktokVideoCampaignReport':
      return reportable.followerCount;
    case 'YoutubeVideoCampaignReport':
      return reportable.subscriberCount;
    default:
      assertUnreachable(reportable);
  }
}

function CampaignReports() {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation(['common', 'manager']);
  const [brandIds, setBrandIds] = useBrandIdsQueryState();

  const { data, loading, fetchMore } = useQuery(myCampaignReportsDocument, {
    variables: {
      input: {
        brandIds,
      },
      first: 25,
      page: 1,
    },
  });

  const { next, observeElement } = useInfiniteScrollPagination({
    paginator: data?.myCampaignReports ?? {
      data: [],
      paginatorInfo: {
        currentPage: 1,
        hasMorePages: false,
      },
    },
    fetchMore,
  });

  const handleOnBrandChange = useCallback(
    async (event: SelectChangeEvent<string[]>) => {
      const selected = event.target.value;
      if (Array.isArray(selected)) {
        await setBrandIds(selected);
      }
    },
    [setBrandIds],
  );

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid size={12}>
          <PageTitle>{t('manager:navigation.reports')}</PageTitle>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 8,
            lg: 5,
            xl: 4,
          }}>
          <FormControl fullWidth>
            <InputLabel id="filter-brand-label">
              {t('manager:campaign.filter.brand.label')}
            </InputLabel>
            <BrandSelect
              variant="outlined"
              labelId="filter-brand-label"
              label={t('manager:campaign.filter.brand.label')}
              value={brandIds}
              multiple
              onChange={handleOnBrandChange}
            />
          </FormControl>
        </Grid>
        <Grid size={12}>
          <DataGrid
            sx={{
              maxWidth: '90vw',
              overflowX: 'scroll',
            }}
            loading={loading || data === undefined}
            rows={data?.myCampaignReports.data ?? []}
            columns={[
              {
                flex: 1,
                minWidth: 250,
                field: 'campaign.name',
                headerName: t('manager:reports.campaignName'),
                valueGetter: (_, row) => row.campaign.name,
                renderCell: ({ row }) => (
                  <Link
                    to="/campaigns/$id/report"
                    params={{
                      id: row.campaign.id,
                    }}>
                    {row.campaign.name}
                  </Link>
                ),
              },
              {
                width: 120,
                field: 'channel',
                headerName: t('common:campaign.channelType'),
                valueGetter: (_, row) =>
                  getChannelTypeName(row.campaign.typeable.channelType),
                renderCell: ({ row }) => (
                  <ChannelTypeChip
                    channelType={row.campaign.typeable.channelType}
                    size="small"
                  />
                ),
              },
              {
                width: 120,
                field: 'contentType',
                headerName: t('common:campaign.contentType'),
                valueGetter: (_, row) =>
                  getContentTypeNameFromCampaignType(row.campaign.typeable),
                renderCell: ({ row }) => (
                  <ChannelContentTypeChip
                    channelType={row.campaign.typeable.channelType}
                    channelContentType={getContentTypeFromCampaignType(
                      row.campaign.typeable,
                    )}
                    size="small"
                  />
                ),
              },
              {
                width: 150,
                field: 'completedCount',
                headerName: t('common:report.completed'),
                valueGetter: (_, row) =>
                  row.reportable.completedCount.toLocaleString(
                    resolvedLanguage,
                  ),
              },
              {
                width: 120,
                field: 'followerCount',
                headerName: t('common:report.followers'),
                valueGetter: (_, row) =>
                  getFollowerOrSubscriberCount(row.reportable).toLocaleString(
                    resolvedLanguage,
                  ),
              },
              {
                width: 100,
                field: 'cpv',
                headerName: t('common:report.cpv'),
                description: t('common:report.cpvDescription'),
                valueGetter: (_, row) =>
                  row.reportable.cpv
                    ? formatCents(
                        row.reportable.cpv,
                        CURRENCY,
                        resolvedLanguage,
                      )
                    : 'n/a',
              },
              {
                width: 120,
                field: 'rewardValue',
                headerName: t('common:report.reward'),
                valueGetter: (_, row) =>
                  formatCents(
                    row.reportable.rewardValue,
                    CURRENCY,
                    resolvedLanguage,
                  ),
              },
            ]}
            slots={{
              pagination: null,
            }}
          />
        </Grid>
        {data?.myCampaignReports.paginatorInfo.hasMorePages ? (
          <Grid
            textAlign="center"
            alignItems="center"
            justifyContent="center"
            size={12}>
            <Button
              ref={observeElement}
              variant="text"
              loading={loading}
              onClick={next}>
              {t('manager:list.loadMoreButton')}
            </Button>
          </Grid>
        ) : null}
      </Grid>
    </Container>
  );
}
